import {useLazyQuery, useMutation} from '@apollo/client';
import {noteMutations, noteQueries} from 'api/graphql-queries/notes';
import {useAlert} from 'hooks/useAlert';
import {ResponseViewModel} from 'models';
import {NoteCreateUpdateRequest, NoteListingRequest, NotePaginationResponse, NoteResponse} from 'models/notes';
import {useCallback} from 'react';
import {useCursorPaginationQuery} from './useCommon';

export const useUpdateNote = () => {
    const [update, {loading, data}] = useMutation<
        {updateNote: NoteResponse},
        {id: string; input: NoteCreateUpdateRequest}
    >(noteMutations.UPDATE_NOTE);

    return {
        updateNote: update,
        updateNoteLoading: loading,
        updateNoteResponse: data?.updateNote,
    };
};

export const useGetAllNotes = () => {
    const {isLoading, fetchPaginated, upsertItemState, data, removeItemState} = useCursorPaginationQuery<
        NoteResponse, // TItem
        NotePaginationResponse, // TResponse
        NoteListingRequest, // TVariables
        'allNotes' // TKey (key inside response)
    >(noteQueries.GET_ALL_NOTES, 'allNotes');
    return {
        getAllNotesLoading: isLoading,
        getAllNotesResponse: data,
        getAllNotes: fetchPaginated,
        afterCreateUpdateHandler: upsertItemState,
        removeItemState,
    };
};

export const useCreateNote = () => {
    const [create, {data, loading}] = useMutation<{createNote: NoteResponse}, {input: NoteCreateUpdateRequest}>(
        noteMutations.CREATE_NOTE,
    );

    return {
        createNote: create,
        createNoteLoading: loading,
        createNoteResponse: data?.createNote,
    };
};

export const useGetNoteId = () => {
    const [refetch, {loading, data}] = useLazyQuery<{noteById: NoteResponse}, {id: string}>(noteQueries.GET_NOTE_BY_ID);

    return {
        getItemById: refetch,
        getItemByIdLoading: loading,
        getItemByIdResponse: data?.noteById,
    };
};

export const useRemoveNote = () => {
    const {invokeAlert} = useAlert();
    const [remove, {data, loading}] = useMutation<{removeNote: ResponseViewModel<string>}, {noteId: string}>(
        noteMutations.REMOVE_NOTE,
    );

    const removeNote = useCallback(
        (id: string) => {
            remove({
                variables: {noteId: id},
            }).catch(error => {
                invokeAlert(error?.message ?? 'Failed to remove reminder');
            });
        },
        [remove],
    );

    return {
        removeNote,
        removeNoteLoading: loading,
        removeNoteResponse: data?.removeNote,
    };
};
